export const isCharValidForDocType = (
  documentTypeAadeCode: string,
  prodSaleCatAadeId: string | undefined,
  saleCharAadeCode: string | undefined,
): boolean => {
  if (!saleCharAadeCode) return false;
  const res = docTypeCharacterizationsMap[documentTypeAadeCode];
  if (!res?.allowedCharacterizationAadeCodes?.length) return false;
  if (res.allowedSaleCategoryAadeIds?.length) {
    if (!prodSaleCatAadeId || !res.allowedSaleCategoryAadeIds.includes(prodSaleCatAadeId)) return false;
  }
  return res.allowedCharacterizationAadeCodes.includes(saleCharAadeCode);
};

export const getProdCharacterizationAadeCodeToSet = (
  documentTypeAadeCode: string,
  prodSaleCatAadeId: string | undefined,
  prodSaleCharAadeCode: string | undefined,
) => {
  // console.log('==========================================');
  // console.log('documentTypeAadeCode: ', documentTypeAadeCode);
  // console.log('prodSaleCatAadeId: ', prodSaleCatAadeId);
  // console.log('prodSaleCharAadeCode: ', prodSaleCharAadeCode);
  // console.log('-----------------');

  if (documentTypeAadeCode) {
    const res = docTypeCharacterizationsMap[documentTypeAadeCode];
    if (res) {
      if (!res.prodCharacterization?.aadeCode) return null;

      // Only apply when the sale category is one that allows this characterization
      if (res.allowedSaleCategoryAadeIds?.length) {
        if (!prodSaleCatAadeId || !res.allowedSaleCategoryAadeIds.includes(prodSaleCatAadeId)) {
          return null;
        }
      }

      // If the current characterization is already valid for this doc type, leave it untouched
      if (res.allowedCharacterizationAadeCodes?.length) {
        if (prodSaleCharAadeCode && res.allowedCharacterizationAadeCodes.includes(prodSaleCharAadeCode)) {
          return null;
        }
      }

      return res.prodCharacterization.aadeCode;
    }

    return null;
  }

  return null;
};

interface CharMap {
  docType: {
    aadeCode: string;
    name: string;
    description?: string;
  };
  prodCharacterization?: {
    aadeCode: string;
    name: string;
    description?: string;
  };
  // Auto-set only fires when the line's sale category aadeId is in this list.
  // Values match the aadeId field on SaleCategory (e.g. 'category1_1').
  // Derived from syndiasmoi_xaraktirismwn_v2.0.2.xlsx.
  allowedSaleCategoryAadeIds?: string[];
  // Full list of valid E3 characterization codes for this doc type (union across
  // all allowedSaleCategoryAadeIds). If the line's current characterization is
  // already in this list it is considered valid and will not be overridden.
  allowedCharacterizationAadeCodes?: string[];
}

/**
 * # Αυτόματος Ορισμός Χαρακτηρισμού Πώλησης ανά Τύπο Παραστατικού
 *
 * ## Σκοπός
 * Το mapping αυτό ορίζει τη λογική αυτόματης ενημέρωσης του χαρακτηρισμού πώλησης (E3)
 * κάθε γραμμής παραστατικού, βάσει του τύπου παραστατικού που επιλέγει ο χρήστης.
 * Στόχος είναι η αποφυγή λανθασμένων χαρακτηρισμών κατά την αλλαγή τύπου (π.χ. από
 * εγχώριο τιμολόγιο σε ενδοκοινοτικό), χωρίς να απαιτείται χειροκίνητη διόρθωση.
 *
 * ## Πηγή
 * Βασίζεται στο αρχείο `syndiasmoi_xaraktirismwn_v2.0.2.xlsx` (ΑΑΔΕ, v2.0.2),
 * το οποίο ορίζει τους έγκυρους συνδυασμούς κατηγορίας εσόδων / χαρακτηρισμού ανά τύπο παραστατικού.
 * Επιβεβαιώθηκε ότι οι συνδυασμοί παραμένουν ίδιοι σε σχέση με προγενέστερη έκδοση (v2.0.0), με μία
 * εξαίρεση: το 5.2/category1_5 απέκτησε νέο επιτρεπτό χαρακτηρισμό (E3_561_002, σημειωμένο ως "νέο"
 * στο ίδιο το αρχείο v2.0.2).
 *
 * ## Λογική Λειτουργίας
 * Για κάθε γραμμή παραστατικού, κατά την αλλαγή τύπου παραστατικού:
 *
 * 1. Εάν η **κατηγορία πώλησης** (aadeId) δεν βρίσκεται στο `allowedSaleCategoryAadeIds`
 *    → δεν γίνεται καμία αλλαγή (π.χ. γραμμές πάγιων, αυτοπαραδόσεων, πληροφοριακών)
 *
 * 2. Εάν ο **τρέχων χαρακτηρισμός** βρίσκεται ήδη στο `allowedCharacterizationAadeCodes`
 *    → δεν γίνεται καμία αλλαγή (ο χαρακτηρισμός είναι ήδη έγκυρος για τον τύπο)
 *
 * 3. Σε κάθε άλλη περίπτωση, εφαρμόζεται ο `prodCharacterization` (ο προεπιλεγμένος χαρακτηρισμός)
 *
 * ### Μνήμη αρχικού χαρακτηρισμού (Restore Logic)
 * Κατά την πρώτη αυτόματη αλλαγή, αποθηκεύεται ο αρχικός χαρακτηρισμός της γραμμής.
 * Εάν ο χρήστης επιστρέψει σε τύπο παραστατικού για τον οποίο ο αρχικός χαρακτηρισμός
 * είναι έγκυρος, αποκαθίσταται αυτόματα ο αρχικός — αντί να εφαρμοστεί ο προεπιλεγμένος.
 *
 * ⚠️ Περιορισμός: Εάν ο χρήστης αλλάξει χειροκίνητα τον χαρακτηρισμό μετά από αυτόματη
 * αλλαγή, η χειροκίνητη επιλογή δεν αποθηκεύεται ως νέος "αρχικός".
 *
 * ---
 * ## Λεξιλόγιο
 *
 * ### Κατηγορίες Εσόδων (Income Sale Categories — aadeId)
 * | aadeId       | Ελληνική Ονομασία                             |
 * |--------------|-----------------------------------------------|
 * | category1_1  | Έσοδα από Πώληση Εμπορευμάτων                 |
 * | category1_2  | Έσοδα από Πώληση Προϊόντων                    |
 * | category1_3  | Έσοδα από Παροχή Υπηρεσιών                    |
 * | category1_4  | Έσοδα από Πώληση Παγίων                       |
 * | category1_5  | Λοιπά Έσοδα / Κέρδη                           |
 * | category1_6  | Αυτοπαραδόσεις / Ιδιοχρησιμοποιήσεις         |
 * | category1_7  | Έσοδα για Λογαριασμό Τρίτων                   |
 * | category1_95 | Λοιπά Πληροφοριακά Στοιχεία Εσόδων           |
 *
 * ### Χαρακτηρισμοί Πώλησης (Sale Characterizations — aadeCode)
 * | aadeCode   | Ελληνική Ονομασία                                                                              |
 * |------------|------------------------------------------------------------------------------------------------|
 * | E3_561_001 | Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών                                      |
 * | E3_561_002 | Πωλήσεις αγαθών και υπηρεσιών Χονδρικές βάσει άρθρου 39α παρ 5 Κώδικα Φ.Π.Α. (Ν.2859/2000)  |
 * | E3_561_003 | Πωλήσεις αγαθών και υπηρεσιών Λιανικές - Ιδιωτική Πελατεία                                    |
 * | E3_561_004 | Πωλήσεις αγαθών και υπηρεσιών Λιανικές βάσει άρθρου 39α παρ 5 Κώδικα Φ.Π.Α. (Ν.2859/2000)   |
 * | E3_561_005 | Πωλήσεις αγαθών και υπηρεσιών Εξωτερικού Ενδοκοινοτικές                                       |
 * | E3_561_006 | Πωλήσεις αγαθών και υπηρεσιών Εξωτερικού Τρίτες Χώρες                                         |
 * | E3_561_007 | Πωλήσεις αγαθών και υπηρεσιών Λοιπά                                                           |
 * | E3_562     | Λοιπά συνήθη έσοδα                                                                             |
 * | E3_563     | Πιστωτικοί τόκοι και συναφή έσοδα                                                              |
 * | E3_570     | Ασυνήθη έσοδα και κέρδη                                                                        |
 * | E3_595     | Έξοδα σε ιδιοπαραγωγή                                                                         |
 *
 * ---
 * ## Αναλυτικό Mapping ανά Τύπο Παραστατικού
 *
 * ### 1.1 — Τιμολόγιο Πώλησης
 * - **Προεπιλεγμένος χαρακτηρισμός:** E3_561_001 — Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών
 * - **Ενεργοποιείται για κατηγορίες:**
 *   - category1_1 — Έσοδα από Πώληση Εμπορευμάτων
 *   - category1_2 — Έσοδα από Πώληση Προϊόντων
 *   - category1_3 — Έσοδα από Παροχή Υπηρεσιών
 * - **Έγκυροι χαρακτηρισμοί (δεν αντικαθίστανται):**
 *   - E3_561_001 — Πωλήσεις Χονδρικές - Επιτηδευματιών
 *   - E3_561_002 — Πωλήσεις Χονδρικές βάσει άρθρου 39α
 *   - E3_561_007 — Πωλήσεις Λοιπά
 *   - E3_563     — Πιστωτικοί τόκοι (μόνο category1_3 στο Excel, union στο mapping)
 * - **Εκτός αυτόματης λογικής:**
 *   - category1_4 (Πώληση Παγίων) → E3_880_001 Πωλήσεις Παγίων Χονδρικές
 *   - category1_7 (Για λογ/σμο τρίτων) → E3_881_001/003/004
 *   - category1_5 (Λοιπά Έσοδα) → E3_561_007 κ.ά.
 *   - category1_95 (Πληροφοριακά) → E3_596/597 ή κενό
 *
 * ### 1.2 — Τιμολόγιο Πώλησης / Ενδοκοινοτικές Παραδόσεις
 * - **Προεπιλεγμένος χαρακτηρισμός:** E3_561_005 — Πωλήσεις αγαθών και υπηρεσιών Εξωτερικού Ενδοκοινοτικές
 * - **Ενεργοποιείται για κατηγορίες:** category1_1, category1_2, category1_3, category1_5
 * - **Έγκυροι χαρακτηρισμοί:** E3_561_005, E3_561_007, E3_562, E3_570
 * - **Εκτός αυτόματης λογικής:**
 *   - category1_4 → E3_880_003 Πωλήσεις Παγίων Εξωτερικού Ενδοκοινοτικές
 *   - category1_7 → E3_881_003 Πωλήσεις για λογ/σμο Τρίτων Εξωτερικού Ενδοκοινοτικές
 *   - category1_95 → χωρίς ενημέρωση E3
 *
 * ### 1.3 — Τιμολόγιο Πώλησης / Παραδόσεις Τρίτων Χωρών
 * - **Προεπιλεγμένος χαρακτηρισμός:** E3_561_006 — Πωλήσεις αγαθών και υπηρεσιών Εξωτερικού Τρίτες Χώρες
 * - **Ενεργοποιείται για κατηγορίες:** category1_1, category1_2, category1_3, category1_5
 * - **Έγκυροι χαρακτηρισμοί:** E3_561_006, E3_561_007, E3_562, E3_570
 * - **Εκτός αυτόματης λογικής:**
 *   - category1_4 → E3_880_004 Πωλήσεις Παγίων Εξωτερικού Τρίτες Χώρες
 *   - category1_7 → E3_881_004 Πωλήσεις για λογ/σμο Τρίτων Εξωτερικού Τρίτες Χώρες
 *   - category1_95 → χωρίς ενημέρωση E3
 *
 * ### 2.1 — Τιμολόγιο Παροχής
 * - **Προεπιλεγμένος χαρακτηρισμός:** E3_561_001 — Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών
 * - **Ενεργοποιείται για κατηγορίες:**
 *   - category1_3 — Έσοδα από Παροχή Υπηρεσιών (μόνη έγκυρη κατηγορία εσόδων για τιμολόγιο παροχής)
 * - **Έγκυροι χαρακτηρισμοί:** E3_561_001, E3_561_002, E3_561_007, E3_563
 * - **Εκτός αυτόματης λογικής:**
 *   - category1_5 (Λοιπά Έσοδα) → E3_561_007, E3_562, E3_563 κ.ά.
 *   - category1_95 → χωρίς ενημέρωση E3
 *
 * ### 2.2 — Τιμολόγιο Παροχής / Ενδοκοινοτική Παροχή Υπηρεσιών
 * - **Προεπιλεγμένος χαρακτηρισμός:** E3_561_005 — Πωλήσεις αγαθών και υπηρεσιών Εξωτερικού Ενδοκοινοτικές
 * - **Ενεργοποιείται για κατηγορίες:** category1_3, category1_5
 * - **Έγκυροι χαρακτηρισμοί:** E3_561_005, E3_561_007, E3_562, E3_563, E3_570
 * - **Εκτός αυτόματης λογικής:** category1_95 → χωρίς ενημέρωση E3
 *
 * ### 2.3 — Τιμολόγιο Παροχής / Παροχή Υπηρεσιών Τρίτων Χωρών
 * - **Προεπιλεγμένος χαρακτηρισμός:** E3_561_006 — Πωλήσεις αγαθών και υπηρεσιών Εξωτερικού Τρίτες Χώρες
 * - **Ενεργοποιείται για κατηγορίες:** category1_3, category1_5
 * - **Έγκυροι χαρακτηρισμοί:** E3_561_006, E3_561_007, E3_562, E3_563, E3_570
 * - **Εκτός αυτόματης λογικής:** category1_95 → χωρίς ενημέρωση E3
 *
 * ### 11.1 — ΑΛΠ (Απόδειξη Λιανικής Πώλησης)
 * - **Προεπιλεγμένος χαρακτηρισμός:** E3_561_003 — Πωλήσεις αγαθών και υπηρεσιών Λιανικές - Ιδιωτική Πελατεία
 * - **Ενεργοποιείται για κατηγορίες:**
 *   - category1_1 — Έσοδα από Πώληση Εμπορευμάτων
 *   - category1_2 — Έσοδα από Πώληση Προϊόντων
 *   - category1_3 — Έσοδα από Παροχή Υπηρεσιών
 * - **Έγκυροι χαρακτηρισμοί:** E3_561_003, E3_561_004, E3_561_005, E3_561_006, E3_561_007, E3_563
 * - **Εκτός αυτόματης λογικής:**
 *   - category1_4 → E3_880_002/003/004 Πωλήσεις Παγίων Λιανικές / Εξωτερικού, E3_561_007
 *   - category1_5 (Λοιπά Έσοδα) → E3_561_007, E3_562, E3_563, E3_570
 *   - category1_6 (Αυτοπαραδόσεις) → E3_595 Έξοδα σε ιδιοπαραγωγή
 *   - category1_7 → E3_881_002/003/004
 *   - category1_95 → χωρίς ενημέρωση E3
 *
 * ### 11.2 — ΑΠΥ (Απόδειξη Παροχής Υπηρεσιών)
 * - **Προεπιλεγμένος χαρακτηρισμός:** E3_561_003 — Πωλήσεις αγαθών και υπηρεσιών Λιανικές - Ιδιωτική Πελατεία
 * - **Ενεργοποιείται για κατηγορίες:**
 *   - category1_3 — Έσοδα από Παροχή Υπηρεσιών
 * - **Έγκυροι χαρακτηρισμοί:** E3_561_003, E3_561_005, E3_561_006, E3_561_007, E3_563
 * - **Εκτός αυτόματης λογικής:**
 *   - category1_5 → E3_561_007, E3_562, E3_563, E3_570
 *   - category1_6 (Αυτοπαραδόσεις) → E3_595 Έξοδα σε ιδιοπαραγωγή
 *   - category1_95 → χωρίς ενημέρωση E3
 *
 * ---
 * ## Τύποι χωρίς αυτόματο χαρακτηρισμό
 * | Κωδικός | Ονομασία                                      | Αιτία                                                              |
 * |---------|-----------------------------------------------|--------------------------------------------------------------------|
 * | 1.12    | (Deprecated — δεν υπάρχει στο v2.0.2 ΑΑΔΕ)  | —                                                                  |
 * | 3.1     | Τίτλος Κτήσης (μη υπόχρεος Εκδότης)         | Μόνο έξοδα (category2_*) — χωρίς έσοδα E3 για τον εκδότη        |
 * | 5.1     | Πιστωτικό Τιμολόγιο / Συσχετιζόμενο         | Κληρονομεί τους έγκυρους χαρακτηρισμούς του συσχετιζόμενου       |
 * | 5.2     | Πιστωτικό Τιμολόγιο / Μη Συσχετιζόμενο      | Πολλαπλοί έγκυροι χαρακτηρισμοί ανά κατηγορία — χειροκίνητη επιλογή |
 * | 6.1     | Στοιχείο Αυτοπαράδοσης                       | Μόνο category1_6 → E3_595, δεν καλύπτεται από το γενικό mapping  |
 * | 9.3     | Δελτίο Αποστολής                              | category3 (Διακίνηση) — δεν ενημερώνει E3 βάσει προδιαγραφής     |
 * | 11.4    | Πιστωτικό Στοιχείο Λιανικής                  | Πολλαπλοί έγκυροι χαρακτηρισμοί ανά κατηγορία — χειροκίνητη επιλογή |
 */
const docTypeCharacterizationsMap: Record<string, CharMap> = {
  '1.1': {
    docType: {
      aadeCode: '1.1',
      name: 'INVOICE',
    },
    prodCharacterization: {
      aadeCode: 'E3_561_001',
      name: 'SALES_GOODS_SERVICES_WHOLESALE_COMMERCIAL',
      description: 'Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών',
    },
    // category1_4 → E3_880_001; category1_7 → E3_881_*; category1_5 → E3_561_007; category1_95 → E3_596/597
    allowedSaleCategoryAadeIds: ['category1_1', 'category1_2', 'category1_3'],
    // Union of allowed E3 codes across category1_1/2/3 (from Excel sheet 1.1)
    allowedCharacterizationAadeCodes: ['E3_561_001', 'E3_561_002', 'E3_561_007', 'E3_563'],
  },
  '1.2': {
    docType: {
      aadeCode: '1.2',
      name: 'INVOICE_INTRA_COMMUNITY_DELIVERIES',
    },
    prodCharacterization: {
      aadeCode: 'E3_561_005',
      name: 'SALES_GOODS_SERVICES_FOREIGN_INTRA_COMMUNITY',
      description: 'Πωλήσεις αγαθών και υπηρεσιών Εξωτερικού Ενδοκοινοτικές',
    },
    // category1_4 → E3_880_003; category1_7 → E3_881_003; category1_95 → no E3
    allowedSaleCategoryAadeIds: ['category1_1', 'category1_2', 'category1_3', 'category1_5'],
    // Union of allowed E3 codes across category1_1/2/3/5 (from Excel sheet 1.2)
    allowedCharacterizationAadeCodes: ['E3_561_005', 'E3_561_007', 'E3_562', 'E3_570'],
  },
  '1.3': {
    docType: {
      aadeCode: '1.3',
      name: 'INVOICE_THIRD_COUNTRIES_DELIVERIES',
    },
    prodCharacterization: {
      aadeCode: 'E3_561_006',
      name: 'SALES_GOODS_SERVICES_FOREIGN_THIRD_COUNTRIES',
      description: 'Πωλήσεις αγαθών και υπηρεσιών Εξωτερικού Τρίτες Χώρες',
    },
    // category1_4 → E3_880_004; category1_7 → E3_881_004; category1_95 → no E3
    allowedSaleCategoryAadeIds: ['category1_1', 'category1_2', 'category1_3', 'category1_5'],
    // Union of allowed E3 codes across category1_1/2/3/5 (from Excel sheet 1.3)
    allowedCharacterizationAadeCodes: ['E3_561_006', 'E3_561_007', 'E3_562', 'E3_570'],
  },
  '1.12': {
    docType: {
      aadeCode: '1.12',
      name: 'INVOICE_FIXED',
    },
    prodCharacterization: undefined,
  },
  '11.1': {
    docType: {
      aadeCode: '11.1',
      name: 'RECEIPT',
    },
    prodCharacterization: {
      aadeCode: 'E3_561_003',
      name: 'SALES_GOODS_SERVICES_RETAIL_PRIVATE_CUSTOMERS',
      description: 'Πωλήσεις αγαθών και υπηρεσιών Λιανικές - Ιδιωτική πελατεία',
    },
    // category1_4 → E3_880_002/003/004/E3_561_007; category1_5 → E3_561_007/562/563/570;
    // category1_6 → E3_595; category1_7 → E3_881_002/003/004; category1_95 → no E3
    allowedSaleCategoryAadeIds: ['category1_1', 'category1_2', 'category1_3'],
    // Union of allowed E3 codes across category1_1/2/3 (from Excel sheet 11.1)
    allowedCharacterizationAadeCodes: ['E3_561_003', 'E3_561_004', 'E3_561_005', 'E3_561_006', 'E3_561_007', 'E3_563'],
  },
  '11.2': {
    docType: {
      aadeCode: '11.2',
      name: 'RECEIPT_SERVICES',
    },
    prodCharacterization: {
      aadeCode: 'E3_561_003',
      name: 'SALES_GOODS_SERVICES_RETAIL_PRIVATE_CUSTOMERS',
      description: 'Πωλήσεις αγαθών και υπηρεσιών Λιανικές - Ιδιωτική πελατεία',
    },
    // category1_5 → E3_561_007/562/563/570; category1_6 → E3_595; category1_95 → no E3
    allowedSaleCategoryAadeIds: ['category1_3'],
    // Allowed E3 codes for category1_3 (from Excel sheet 11.2)
    allowedCharacterizationAadeCodes: ['E3_561_003', 'E3_561_005', 'E3_561_006', 'E3_561_007', 'E3_563'],
  },
  '11.4': {
    docType: {
      aadeCode: '11.4',
      name: 'RECEIPT_RETURN_RELATED',
    },
    prodCharacterization: undefined,
  },
  '2.1': {
    docType: {
      aadeCode: '2.1',
      name: 'INVOICE_SERVICES',
    },
    prodCharacterization: {
      aadeCode: 'E3_561_001',
      name: 'SALES_GOODS_SERVICES_WHOLESALE_COMMERCIAL',
      description: 'Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών',
    },
    // For service invoices only category1_3 and category1_5 appear; E3_561_001 only valid for category1_3
    allowedSaleCategoryAadeIds: ['category1_3'],
    // Allowed E3 codes for category1_3 (from Excel sheet 2.1)
    allowedCharacterizationAadeCodes: ['E3_561_001', 'E3_561_002', 'E3_561_007', 'E3_563'],
  },
  '2.2': {
    docType: {
      aadeCode: '2.2',
      name: 'INVOICE_SERVICES_INTRA_COMMUNITY',
    },
    prodCharacterization: {
      aadeCode: 'E3_561_005',
      name: 'SALES_GOODS_SERVICES_FOREIGN_INTRA_COMMUNITY',
      description: 'Πωλήσεις αγαθών και υπηρεσιών Εξωτερικού Ενδοκοινοτικές',
    },
    // category1_95 → no E3
    allowedSaleCategoryAadeIds: ['category1_3', 'category1_5'],
    // Union of allowed E3 codes across category1_3/5 (from Excel sheet 2.2)
    allowedCharacterizationAadeCodes: ['E3_561_005', 'E3_561_007', 'E3_562', 'E3_563', 'E3_570'],
  },
  '2.3': {
    docType: {
      aadeCode: '2.3',
      name: 'INVOICE_SERVICES_THIRD_COUNTRIES',
    },
    prodCharacterization: {
      aadeCode: 'E3_561_006',
      name: 'SALES_GOODS_SERVICES_FOREIGN_THIRD_COUNTRIES',
      description: 'Πωλήσεις αγαθών και υπηρεσιών Εξωτερικού Τρίτες Χώρες',
    },
    // category1_95 → no E3
    allowedSaleCategoryAadeIds: ['category1_3', 'category1_5'],
    // Union of allowed E3 codes across category1_3/5 (from Excel sheet 2.3)
    allowedCharacterizationAadeCodes: ['E3_561_006', 'E3_561_007', 'E3_562', 'E3_563', 'E3_570'],
  },
  '3.1': {
    docType: {
      aadeCode: '3.1',
      name: 'TITLE_DEED_NON_OBLIGATORY_ISSUER',
    },
    prodCharacterization: undefined,
  },
  '5.1': {
    docType: {
      aadeCode: '5.1',
      name: 'INVOICE_RETURN_RELATED',
    },
    // Inherits allowed combinations from the related document's type
    prodCharacterization: undefined,
  },
  '5.2': {
    docType: {
      aadeCode: '5.2',
      name: 'INVOICE_RETURN_UNRELATED',
      description: 'Πιστωτικό Τιμολόγιο / Μη Συσχετιζόμενο',
    },
    // No default: allowed codes span E3_561_001/002/005/006/007 depending on the
    // original invoice's category — no single characterization is universally correct.
    // (E3_561_002 for category1_5 is a new addition in v2.0.2, per the source file's own "νέο" marker.)
    prodCharacterization: undefined,
  },
  '6.1': {
    docType: {
      aadeCode: '6.1',
      name: 'SELF_DELIVERY_ITEM',
    },
    prodCharacterization: undefined,
  },
  '9.3': {
    docType: {
      aadeCode: '9.3',
      name: 'DELIVERY_NOTE',
    },
    // Delivery notes do not update E3 per spec
    prodCharacterization: undefined,
  },
};
